import React from "react";
import { Layers, Edit } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import Button from "../../../../components/ui/Button";
import { useTranslation } from "react-i18next";
import { getTranslatedCategoryName, getTranslatedSubcategories } from "../../../../utils/categoryTranslation";

interface Category {
    id: string;
    name: string;
    description: string;
    subcategories: string[]; // Backend sends subcategories as string array
    translatedName?: Record<string, string>; // Translations: { it: "Nome italiano", es: "Nombre español", ... }
    translatedSubCategories?: Record<string, string[]>; // Translations: { it: ["Sottocategoria1", ...], es: [...], ... }
    isEmergency?: boolean;
    isRequireVerification?: boolean;
    type?: { id: string | number };
}

interface Subcategory {
    id: string; // Use subcategory name as ID since backend doesn't provide separate IDs
    name: string;
    description?: string;
}

interface CategoryDisplayProps {
    selectedCategories: Category[];
    selectedSubcategories: Subcategory[];
    onEdit?: () => void;
    showEditIcon?: boolean;
}

const CategoryDisplay: React.FC<CategoryDisplayProps> = ({
    selectedCategories,
    selectedSubcategories,
    onEdit,
    showEditIcon = false
}) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language.split("-")[0];
    if (selectedCategories.length === 0 && selectedSubcategories.length === 0) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 mb-2">{t("categoryDisplay.noCategoriesSelected")}</p>
                <p className="text-sm text-gray-400">{t("categoryDisplay.addCategoriesHelp")}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Categories */}
            {selectedCategories.length > 0 && (
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                            <Layers className="w-5 h-5 text-blue-600" />
                            {t("categoryDisplay.serviceCategories")}
                        </h3>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                        {selectedCategories.map((category) => {
                            const translatedName = getTranslatedCategoryName(category, currentLang);
                            const originalSubcats = category.subcategories || [];
                            
                            // Get translations - handle both object and array formats
                            const translationsRaw = category.translatedSubCategories?.[currentLang];
                            let translatedSubcatsMap: Record<string, string> = {};
                            
                            if (translationsRaw) {
                                if (Array.isArray(translationsRaw)) {
                                    // Array format: ['Installazione', 'Riparazione', ...]
                                    originalSubcats.forEach((original, index) => {
                                        if (translationsRaw[index]) {
                                            translatedSubcatsMap[original] = translationsRaw[index];
                                        }
                                    });
                                } else if (typeof translationsRaw === "object") {
                                    // Object format: {Installation: 'Installazione', Repair: 'Riparazione', ...}
                                    translatedSubcatsMap = translationsRaw;
                                }
                            }
                            
                            return (
                            <div
                                key={category.id}
                                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-medium text-gray-900">{translatedName}</h4>
                                    <div className="flex gap-1">
                                        {category.isEmergency && (
                                            <Badge variant="destructive" className="text-xs">
                                                {t("categoryDisplay.emergency")}
                                            </Badge>
                                        )}
                                        {/* {category.isRequireVerification && (
                                            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                                                {t("categoryDisplay.verificationRequired")}
                                            </Badge>
                                        )} */}
                                    </div>
                                </div>
                                {/* <p className="text-sm text-gray-600 mb-3">{category.description}</p> */}
                                
                                {/* Show subcategories for this category */}
                                {category.subcategories && category.subcategories.length > 0 && (
                                    <div>
                                        <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                                            {t("categoryDisplay.subcategories")}
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {originalSubcats
                                                .filter(subName => {
                                                    // Check if this subcategory name exists in selectedSubcategories
                                                    return selectedSubcategories.some(selected => 
                                                        selected.name === subName || 
                                                        selected.name === subName.toString()
                                                    );
                                                })
                                                .map((subcategoryName, index) => {
                                                    // Get translated name from the map, fallback to original
                                                    const displayName = translatedSubcatsMap[subcategoryName] || subcategoryName;
                                                    return (
                                                    <Badge
                                                        key={`${category.id}-${index}`}
                                                        variant="outline"
                                                        className="text-xs"
                                                    >
                                                        {displayName}
                                                    </Badge>
                                                );})}
                                        </div>
                                    </div>
                                )}
                                

                            </div>
                        );})}
                    </div>
                </div>
            )}



        </div>
    );
};

export default CategoryDisplay;
